import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, supabaseConfigured } from '../lib/supabase.js';
import { useAuth } from '../lib/auth.js';
import { tmdb, posterUrl } from '../lib/tmdb.js';
import { formatMemberNumber } from '../lib/member.js';
import genres from '../config/genres.json';

// Map TMDB genre_id → display name from our config. Includes both movie and
// TV genre IDs (they overlap mostly). Anything not in the map gets the raw
// TMDB name when we have it.
const GENRE_NAMES = (() => {
  const m = new Map();
  for (const a of genres.aisles) if (a.tmdbGenreId) m.set(a.tmdbGenreId, a.name);
  // Add a few common TMDB genres that aren't in our aisles
  m.set(12,  'Adventure');
  m.set(16,  'Animation');
  m.set(80,  'Crime');
  m.set(14,  'Fantasy');
  m.set(36,  'History');
  m.set(10402, 'Music');
  m.set(9648, 'Mystery');
  m.set(10752, 'War');
  m.set(37,  'Western');
  return m;
})();

export default function Members() {
  const { profile: me } = useAuth();
  const [members, setMembers] = useState(null);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!supabaseConfigured) return;
    let alive = true;

    (async () => {
      try {
        // 1. Pull all profiles in member-number order.
        const { data: profiles, error: pe } = await supabase
          .from('profiles')
          .select('user_id, member_number, slug, display_name, is_admin, created_at')
          .order('member_number');
        if (pe) throw pe;

        // 2. Pull every LOVE (not ejects) and group by user_id. Without the
        //    kind filter, ejects would pollute the recent-posters strip and
        //    the loves count on each member card.
        const { data: likes } = await supabase
          .from('likes')
          .select('user_id, tmdb_id, media_type, created_at')
          .eq('kind', 'love')
          .order('created_at', { ascending: false });
        const byUser = new Map();
        for (const l of likes || []) {
          if (!byUser.has(l.user_id)) byUser.set(l.user_id, []);
          byUser.get(l.user_id).push(l);
        }

        // 3. For each member, hydrate up to 4 most recent loves so we can
        //    show poster thumbs and tally top genres.
        const enriched = await Promise.all(profiles.map(async (p) => {
          const all = byUser.get(p.user_id) || [];
          const recent = all.slice(0, 4);
          const details = await Promise.all(
            recent.map((l) => tmdb.media(l.tmdb_id, l.media_type || 'movie').catch(() => null))
          );

          // Tally top 3 genres across ALL loves (not just recent), so the
          // top-genres list is representative even if we only show 4 posters.
          // We need genre_ids for each loved title — we already have those for
          // the recent ones. For others we'd need extra fetches; to keep this
          // page fast we approximate using the recent set. Good enough at
          // small scale.
          const genreCounts = new Map();
          for (const d of details.filter(Boolean)) {
            for (const g of (d.genres || []).map((g) => g.id)) {
              genreCounts.set(g, (genreCounts.get(g) || 0) + 1);
            }
          }
          const topGenres = [...genreCounts.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([id]) => GENRE_NAMES.get(id) || null)
            .filter(Boolean);

          return {
            ...p,
            lovesCount: all.length,
            recentPosters: details.filter(Boolean).map((d) => d.poster_path),
            topGenres,
          };
        }));

        if (alive) setMembers(enriched);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load members.');
      }
    })();

    return () => { alive = false; };
  }, []);

  // Insert "Reserved" placeholders for gaps in the member-number sequence,
  // but only show them to admins. Visitors see the real members only.
  const withReserved = useMemo(() => {
    if (!members) return null;
    if (!me?.is_admin) return members;
    const out = [];
    let prev = 0;
    for (const m of members) {
      for (let n = prev + 1; n < m.member_number; n++) {
        out.push({ reserved: true, member_number: n });
      }
      out.push(m);
      prev = m.member_number;
    }
    return out;
  }, [members, me?.is_admin]);

  return (
    <div className="hb-members">
      <header className="hb-members__header">
        <div className="hb-members__eyebrow">Home Blockbuster</div>
        <h1 className="hb-members__title">Membership Roster</h1>
        {members && (
          <p className="hb-members__sub">
            {members.length} {members.length === 1 ? 'member' : 'members'} in good standing
          </p>
        )}
      </header>

      {error && <div className="hb-empty">{error}</div>}

      {!members && !error && (
        <div className="hb-empty">Pulling the rolodex…</div>
      )}

      {withReserved && (
        <ul className="hb-members__list">
          {withReserved.map((m) => (
            m.reserved
              ? <ReservedCard key={'r' + m.member_number} number={m.member_number} />
              : <MemberCard key={m.user_id} member={m} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberCard({ member }) {
  const joined = relativeDate(member.created_at);
  return (
    <li className="hb-member-card">
      <Link to={`/u/${member.slug}`} className="hb-member-card__link">
        <div className="hb-member-card__main">
          <div className="hb-member-card__number">{formatMemberNumber(member.member_number)}</div>
          <div className="hb-member-card__body">
            <div className="hb-member-card__name-row">
              <h2 className="hb-member-card__name">{member.display_name}</h2>
              {member.is_admin && <span className="hb-member-card__chip">ADMIN</span>}
            </div>
            <div className="hb-member-card__meta">
              /u/{member.slug} · joined {joined} · {member.lovesCount} {member.lovesCount === 1 ? 'love' : 'loves'}
            </div>
            {member.topGenres.length > 0 && (
              <div className="hb-member-card__taste">
                Mostly: <strong>{member.topGenres.join(' · ')}</strong>
              </div>
            )}
          </div>
        </div>
        <div className="hb-member-card__thumbs">
          {member.recentPosters.length === 0 ? (
            <span className="hb-member-card__thumbs-empty">No rentals yet</span>
          ) : member.recentPosters.map((p, i) => (
            <img key={i} src={posterUrl(p, 'w92')} alt="" loading="lazy" />
          ))}
        </div>
      </Link>
    </li>
  );
}

function ReservedCard({ number }) {
  return (
    <li className="hb-member-card hb-member-card--reserved">
      <div className="hb-member-card__main">
        <div className="hb-member-card__number hb-member-card__number--dim">
          {formatMemberNumber(number)}
        </div>
        <div className="hb-member-card__body">
          <h2 className="hb-member-card__name hb-member-card__name--dim">RESERVED</h2>
          <div className="hb-member-card__meta">Holding this spot for someone special.</div>
        </div>
      </div>
    </li>
  );
}

function relativeDate(iso) {
  if (!iso) return 'recently';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.floor((now - then) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days} days ago`;
  if (days < 30)  return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}
